import { useMemo } from "react";
import { Card, EntityEditScreen, useEntityDetail } from "@pericles/framework-frontend";
import type { UiEntitySpec } from "@pericles/framework-frontend";

import { useGenerateRequirementWithAI } from "../api/requirementsClient";

import { GENERATION_STATUS_BADGES } from "./RequirementsListScreen";
import { renderStatusBadge } from "./RequirementReviewStatusBadge";

interface RequirementEditPanelProps {
  entitySpec: UiEntitySpec;
  id: string | number;
  onSaved?: (record: unknown) => void;
  onBack?: () => void;
  onUpdated?: () => void;
}

interface RequirementRecord {
  id: string | number;
  title?: string;
  generationStatus?: string;
  generatedSummary?: string;
}

export function RequirementEditPanel({
  entitySpec,
  id,
  onSaved,
  onBack,
  onUpdated,
}: RequirementEditPanelProps): JSX.Element {
  const { data, isLoading: isDetailLoading, refetch } = useEntityDetail<RequirementRecord>({
    entity: entitySpec.entityName,
    id,
    enabled: true,
  });

  const { generate, isLoading, error } = useGenerateRequirementWithAI();

  const statusBadge = useMemo(
    () => renderStatusBadge(data?.generationStatus, GENERATION_STATUS_BADGES),
    [data?.generationStatus],
  );

  const handleGenerate = async () => {
    try {
      await generate(id);
      await refetch();
      onUpdated?.();
      alert("AI generation triggered successfully.");
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to trigger AI generation");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Card className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-slate-700">Generation status</span>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              {isDetailLoading ? "Loading…" : statusBadge || "Not available"}
            </div>
            {data?.generatedSummary ? (
              <p className="text-sm text-slate-500 line-clamp-2">{data.generatedSummary}</p>
            ) : null}
            {error ? (
              <span className="text-sm text-red-600">
                {error instanceof Error ? error.message : "Failed to generate requirement"}
              </span>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              className="button button-primary"
              disabled={isLoading}
              onClick={handleGenerate}
            >
              {isLoading ? "Generating…" : "Generate with AI"}
            </button>
          </div>
        </div>
      </Card>

      <EntityEditScreen entitySpec={entitySpec} id={id} onSaved={onSaved} onBack={onBack} />
    </div>
  );
}
